import { Link } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Plus, FileText, Activity, Clock, Columns3, Square, Layers, Frame,
  Hammer, Library, ShieldCheck, Settings, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FootingIcon } from '@/components/icons/FootingIcon';
import { OnboardingModal } from '@/features/onboarding/OnboardingModal';
import { useAuthStore, hasAnyPermission } from '@/stores/authStore';

interface ModuleInfo {
  to: string;
  title: string;
  desc: string;
  icon: typeof Columns3;
  iconCustom?: () => JSX.Element;
  badge?: string;
}

const MODULES: ModuleInfo[] = [
  {
    // Module cards link to each module's index (saved designs list).
    to: '/app/columns',
    title: 'Columnas',
    desc: 'Secciones T, L, +, rectangular y circular. Diagrama P-M 3D interactivo, refuerzo iterativo y diseño por cortante con estribos confinados.',
    icon: Columns3,
  },
  {
    to: '/app/beams',
    title: 'Vigas',
    desc: 'Rectangulares, peraltadas, chatas, soleras, invertidas, arriostre, borde y confinamiento. Flexo-cortante completo + envolventes DMF/DFC.',
    icon: Square,
  },
  {
    to: '/app/slabs',
    title: 'Losas',
    desc: 'Aligeradas y macizas en una o dos direcciones. Marcus para 2D, deflexión, acero por temperatura y espaciamiento máximo.',
    icon: Layers,
  },
  {
    to: '/app/walls',
    title: 'Muros (placa)',
    desc: 'Placas de concreto armado. P-M en el plano, cortante E.060 11.10 y elementos de borde con confinamiento (E.060 21.9).',
    icon: Frame,
  },
  {
    to: '/app/footings',
    title: 'Zapatas',
    desc: 'Aisladas, corridas, combinadas y conectadas. Presión sobre el suelo, cortante 1D, punzonado y flexión a la cara de columna.',
    icon: Columns3,
    iconCustom: () => <FootingIcon size={18} />,
  },
  {
    to: '/app/predim',
    title: 'Predimensionamiento',
    desc: 'Dimensiones preliminares para los 5 tipos de elementos. Hub colapsable con calculadoras rápidas + diagramas SVG y 3D.',
    icon: Hammer,
  },
];

const REFERENCE: ModuleInfo[] = [
  {
    to: '/app/normas',
    title: 'Normas y referencias',
    desc: 'Texto de la E.060 + ACI 318-19 con búsqueda y enlaces cruzados.',
    icon: Library,
  },
];

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  // Admin section is shown when the user holds at least one admin-scope
  // permission (matches the sidebar gating).
  const showAdminCard = hasAnyPermission(user, ['users.read', 'roles.read', 'audit.read']);

  // Greeting based on time of day
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches';
  const firstName = user?.name?.split(' ')[0] ?? user?.email?.split('@')[0] ?? '';

  return (
    <div className="px-8 py-8">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            {greeting}
          </div>
          <h1 className="mb-1 text-3xl">
            Hola, <span style={{ color: 'var(--accent)' }}>{firstName || 'Ingeniero'}</span>
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Tu espacio de trabajo. Crea cálculos, abre proyectos recientes, revisa actividad.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/settings" className="no-underline">
            <Button variant="ghost" size="md">
              <Settings size={16} strokeWidth={1.8} />
              Configuración
            </Button>
          </Link>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-[var(--radius)] px-4 py-2 text-sm font-medium transition-colors hover:opacity-90"
                style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
              >
                <Plus size={16} strokeWidth={2} />
                Nuevo cálculo
                <ChevronDown size={14} strokeWidth={2} />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                className="z-50 min-w-[240px] rounded-[var(--radius-md)] border p-1 shadow-xl"
                style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
              >
                <DropdownMenu.Label
                  className="px-3 py-1.5 text-[10px] uppercase tracking-[0.14em]"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Elige un módulo
                </DropdownMenu.Label>
                {MODULES.map((m) => (
                  <DropdownMenu.Item key={m.to} asChild>
                    <Link
                      to={m.to}
                      className="flex w-full items-center gap-2.5 rounded-[var(--radius-sm)] px-3 py-2 text-[13px] no-underline outline-none transition-colors data-[highlighted]:bg-[var(--surface-2)]"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      <span style={{ color: 'var(--accent)' }}>
                        {m.iconCustom ? m.iconCustom() : <m.icon size={14} strokeWidth={1.8} />}
                      </span>
                      {m.title}
                    </Link>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard
          label="Módulos de cálculo"
          value={String(MODULES.length - 1)}
          hint="Disponibles ahora mismo"
          icon={Activity}
        />
        <KpiCard
          label="Tu rol"
          value={user?.role?.label ?? '—'}
          hint={user?.role?.description || 'Sin descripción'}
          icon={user?.role?.slug === 'admin' ? ShieldCheck : FileText}
        />
        <KpiCard
          label="Cuenta creada"
          value={user?.created_at ? new Date(user.created_at).toLocaleDateString('es-PE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
          hint={user?.email ?? ''}
          icon={Clock}
        />
      </div>

      <section className="mt-10">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
            Módulos disponibles
          </h2>
          <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            E.060 + ACI 318-19
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => (
            <ModuleCard key={m.to} module={m} />
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="mb-4 text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          Recursos
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {REFERENCE.map((m) => (
            <ModuleCard key={m.to} module={m} />
          ))}
          {showAdminCard && (
            <ModuleCard
              module={{
                to: '/app/admin',
                title: 'Administración',
                desc: 'Gestión de usuarios y roles. Auditoría de eventos de la plataforma.',
                icon: ShieldCheck,
                badge: 'ADMIN',
              }}
            />
          )}
        </div>
      </section>

      <div className="mt-10 rounded-[var(--radius-md)] border p-4 text-[12px]"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}>
        <strong style={{ color: 'var(--text-secondary)' }}>AlphaConcreto v0.1.0</strong> — Plataforma de cálculo
        estructural de concreto armado según Norma Peruana E.060 y ACI 318-19. Para soporte o reportar bugs,
        contacta a tu administrador.
      </div>
      {/* First-time tour. Auto-opens once per device; nothing happens on
          subsequent renders. */}
      <OnboardingModal />
    </div>
  );
}

function KpiCard({
  label, value, hint, icon: Icon,
}: {
  label: string;
  value: string;
  hint: string;
  icon: typeof FileText;
}) {
  return (
    <div
      className="rounded-[var(--radius-md)] border p-5"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          {label}
        </span>
        <Icon size={16} strokeWidth={1.6} style={{ color: 'var(--text-muted)' }} />
      </div>
      <div className="font-mono tabular text-2xl" style={{ color: 'var(--text-primary)', lineHeight: 1.1 }}>
        {value}
      </div>
      <div className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
        {hint}
      </div>
    </div>
  );
}

function ModuleCard({ module: m }: { module: ModuleInfo }) {
  return (
    <Link
      to={m.to}
      className="group block rounded-[var(--radius-md)] border p-5 no-underline transition-all duration-200 hover:scale-[1.01] hover:border-[var(--accent)]"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <div className="mb-3 flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-[var(--radius)]"
          style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
        >
          {m.iconCustom ? m.iconCustom() : <m.icon size={18} strokeWidth={1.8} />}
        </div>
        <div className="flex items-baseline justify-between gap-2 w-full min-w-0">
          <h3 className="truncate text-base"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--text-primary)' }}>
            {m.title}
          </h3>
          {m.badge && (
            <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider shrink-0"
              style={{ background: 'rgba(245,158,11,0.18)', color: 'var(--accent)' }}>
              {m.badge}
            </span>
          )}
        </div>
      </div>
      <p className="text-[12.5px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        {m.desc}
      </p>
    </Link>
  );
}
